import {
    Box,
    Typography,
    Paper,
    Button,
    Autocomplete,
    TextField,
    FormControl,
    List,
    ListItem,
    ListItemText,
    IconButton,
    Divider,
    Pagination
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { t } from 'i18next';
import { useQuestionnaires } from '../../../hooks/useQuestionnaires';

const QuestionnaireStep = ({
    selectedQuestionnaires = [],
    initialQuestionnaires = [],
    durationWeeks,
    onAdd,
    onUpdate,
    onRemove,
    onAddInitial,
    onRemoveInitial
}) => {
    const initialQ = useQuestionnaires();
    const scheduledQ = useQuestionnaires();

    const isInScheduled = (uuid) =>
        selectedQuestionnaires.some((q) => q.questionnaireUuid === uuid);

    const isInInitial = (uuid) =>
        initialQuestionnaires.some((q) => q.questionnaireUuid === uuid);

    const renderPagination = ({ paginationModel, setPaginationModel, count }) => (
        <Box display="flex" justifyContent="center" mt={3}>
            <Pagination
                count={Math.ceil(count / paginationModel.pageSize)}
                page={paginationModel.page + 1}
                onChange={(_, newPage) =>
                    setPaginationModel((prev) => ({
                        ...prev,
                        page: newPage - 1
                    }))
                }
            />
        </Box>
    );

    const buildAssignmentOptions = () => {
        const options = [];

        const initialWeeks = [1, 2, 3];

        initialWeeks.forEach((week) => {
            options.push({
                label: `${t('plans.questionnaires.week')} ${week}`,
                weeks: [week]
            });
        });

        const monthOptions = [
            { label: '1 mês (4 semanas)', weeks: [4] },
            { label: '2 meses (8 semanas)', weeks: [8] },
            { label: '3 meses (12 semanas)', weeks: [12] },
            { label: '6 meses (24 semanas)', weeks: [24] },
            { label: '1 ano (52 semanas)', weeks: [52] }
        ];

        options.push(...monthOptions);

        return options;
    };

    const options = buildAssignmentOptions(durationWeeks);

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                {t('plans.steps.questionnaires')}
            </Typography>

            {/* QUESTIONÁRIOS INICIAIS */}
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                <Typography fontWeight="bold" gutterBottom>
                    {t('plans.questionnaires.initial')}
                </Typography>

                <Typography variant="body2" color="text.secondary" mb={2}>
                    {t('plans.questionnaires.initial_helper')}
                </Typography>

                <List dense>
                    {initialQ.data.map((q) => (
                        <ListItem
                            key={q.uuid}
                            secondaryAction={
                                isInInitial(q.uuid) ? (
                                    <IconButton
                                        color="error"
                                        onClick={() => onRemoveInitial(q.uuid)}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                ) : (
                                    <Button
                                        size="small"
                                        variant="contained"
                                        disabled={isInScheduled(q.uuid)}
                                        onClick={() =>
                                            onAddInitial({
                                                questionnaireUuid: q.uuid,
                                                questionnaireName: q.name
                                            })
                                        }
                                    >
                                        {t('plans.buttons.add')}
                                    </Button>
                                )
                            }
                        >
                            <ListItemText primary={q.name} secondary={q.description} />
                        </ListItem>
                    ))}
                </List>

                {renderPagination(initialQ)}
            </Paper>

            <Divider sx={{ my: 3 }} />

            {/* QUESTIONÁRIOS AGENDADOS */}
            <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography fontWeight="bold" gutterBottom>
                    {t('plans.questionnaires.scheduled')}
                </Typography>

                <List dense>
                    {scheduledQ.data.map((q) => (
                        <ListItem
                            key={q.uuid}
                            secondaryAction={
                                <Button
                                    size="small"
                                    variant="contained"
                                    disabled={isInScheduled(q.uuid)}
                                    onClick={() =>
                                        onAdd({
                                            questionnaireUuid: q.uuid,
                                            questionnaireName: q.name,
                                            assignedWeeks: [1]
                                        })
                                    }
                                >
                                    {t('plans.buttons.add')}
                                </Button>
                            }
                        >
                            <ListItemText primary={q.name} secondary={q.description} />
                        </ListItem>
                    ))}
                </List>

                {renderPagination(scheduledQ)}
            </Paper>

            {/* QUESTIONÁRIOS SELECIONADOS */}
            {selectedQuestionnaires.length > 0 && (
                <Paper variant="outlined" sx={{ p: 2, mt: 3 }}>
                    <Typography fontWeight="bold" gutterBottom>
                        {t('plans.questionnaires.selected')}
                    </Typography>

                    <List dense>
                        {selectedQuestionnaires.map((q) => (
                            <ListItem
                                key={q.questionnaireUuid}
                                secondaryAction={
                                    <IconButton
                                        color="error"
                                        onClick={() => onRemove(q.questionnaireUuid)}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                }
                            >
                                <ListItemText primary={q.questionnaireName} />

                                <FormControl size="small" sx={{ minWidth: 240 }}>
                                    <Autocomplete
                                        multiple
                                        size="small"
                                        options={options}
                                        getOptionLabel={(option) => option.label}
                                        value={options.filter((opt) =>
                                            opt.weeks.every((w) =>
                                                q.assignedWeeks.includes(w)
                                            )
                                        )}
                                        onChange={(_, newValue) => {
                                            const weeks = newValue.flatMap(
                                                (opt) => opt.weeks
                                            );
                                            onUpdate(q.questionnaireUuid, weeks);
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label={t(
                                                    'plans.questionnaires.assignmentWeeks'
                                                )}
                                            />
                                        )}
                                    />
                                </FormControl>
                            </ListItem>
                        ))}
                    </List>
                </Paper>
            )}
        </Box>
    );
};

export default QuestionnaireStep;
